import { CheckIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TranslationKey } from "@/lib/i18n/locales";
import { LOCALE_LABELS, SUPPORTED_LOCALES } from "@/lib/i18n/config";
import { useAppStore } from "@/stores/useApp";
import { cn } from "@/lib/utils";

// `list` is the burger's vertical list; `inline` the footer's wrapping row of
// the same buttons (same ids, so page objects scope them to their container:
// below `lg` both render on a page). Each placement names its own heading key.
const LanguageSelector = ({
  onSelect,
  layout = "list",
  titleKey,
}: {
  onSelect?: () => void;
  layout?: "list" | "inline";
  titleKey: TranslationKey;
}) => {
  // i18next already holds the resolved locale (persisted choice or detection),
  // so reading it here keeps one source of truth for "what is showing".
  const { t, i18n } = useTranslation();
  const setLocale = useAppStore((s) => s.setLocale);
  const active = i18n.resolvedLanguage;

  return (
    <div
      data-test-id="language-section"
      className={cn("flex flex-col gap-4", layout === "list" && "mb-8")}
    >
      <h3 className="font-semibold">{t(titleKey)}</h3>
      <ul
        className={cn(
          "flex",
          layout === "inline" ? "flex-wrap gap-x-4 gap-y-1" : "flex-col",
        )}
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <li key={locale}>
            <button
              type="button"
              data-test-id={`language-option-${locale}`}
              onClick={() => {
                setLocale(locale);
                onSelect?.();
              }}
              className={cn(
                "text-neutral-30 flex items-center py-2 text-left transition-colors hover:text-white",
                layout === "inline" ? "gap-1" : "w-full justify-between",
                locale === active && "font-semibold text-white",
              )}
            >
              <span>{LOCALE_LABELS[locale]}</span>
              {locale === active && <CheckIcon className="size-4" />}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LanguageSelector;
